/**
 * MRRBreakdownChart - Donut chart showing MRR components
 *
 * Segments:
 * - New MRR (emerald) - New customers
 * - Expansion (blue) - Upgrades/upsells
 * - Contraction (amber) - Downgrades
 * - Churn (red) - Lost revenue
 *
 * Center displays net new MRR
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import type { MRRBreakdown } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface MRRBreakdownChartProps {
  data: MRRBreakdown;
  className?: string;
}

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
  isNegative?: boolean;
}

// ============================================
// Configuration
// ============================================

const COLORS = {
  new: '#10b981',
  expansion: '#3b82f6',
  contraction: '#f59e0b',
  churn: '#ef4444',
};

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

// ============================================
// Custom Tooltip
// ============================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: ChartDataItem;
  }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  if (!entry) return null;

  const isNegative = entry.payload?.isNegative;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <div className="flex items-center gap-2 mb-1">
        <div
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: entry.payload?.color }}
        />
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {entry.name}
        </span>
      </div>
      <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
        {isNegative ? '-' : '+'}{formatCurrency(Math.abs(entry.value))}
      </p>
    </div>
  );
}

// ============================================
// Custom Legend
// ============================================

interface LegendProps {
  payload?: Array<{
    value: string;
    color: string;
    payload: ChartDataItem;
  }>;
}

function CustomLegend({ payload }: LegendProps) {
  if (!payload) return null;

  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-slate-600 dark:text-slate-400">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Component
// ============================================

export function MRRBreakdownChart({ data, className }: MRRBreakdownChartProps) {
  // Prepare chart data - only show non-zero values
  const chartData: ChartDataItem[] = [
    { name: 'New', value: Math.abs(data.newMrr), color: COLORS.new },
    { name: 'Expansion', value: Math.abs(data.expansionMrr), color: COLORS.expansion },
    { name: 'Contraction', value: Math.abs(data.contractionMrr), color: COLORS.contraction, isNegative: true },
    { name: 'Churn', value: Math.abs(data.churnedMrr), color: COLORS.churn, isNegative: true },
  ].filter((item) => item.value > 0);

  const netMrr = data.netNewMrr;
  const isNetPositive = netMrr >= 0;

  if (chartData.length === 0) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">MRR Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[280px]">
          <p className="text-sm text-slate-500">No data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">MRR Breakdown</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[280px] w-full relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label showing net MRR */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" style={{ top: '-20px' }}>
            <span className="text-xs text-slate-500 dark:text-slate-400">Net New</span>
            <span
              className={cn(
                'text-xl font-bold tabular-nums',
                isNetPositive
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              )}
            >
              {isNetPositive ? '+' : ''}{formatCurrency(netMrr)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function MRRBreakdownChartSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent>
        <div className="h-[280px] flex items-center justify-center">
          <div className="h-44 w-44 rounded-full border-8 border-slate-200 dark:border-slate-700 animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
